import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ─── Rules ───────────────────────────────────────────────
export const fetchEmailRules = createAsyncThunk(
  'emailAutoreply/fetchRules',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/email-autoreply', { params });
      return response.data.rules || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch rules');
    }
  }
);

export const fetchEmailRule = createAsyncThunk(
  'emailAutoreply/fetchRule',
  async ({ ruleId }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/marketing/email-autoreply/${ruleId}`);
      return response.data.rule;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch rule');
    }
  }
);

export const createEmailRule = createAsyncThunk(
  'emailAutoreply/createRule',
  async (ruleData, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/email-autoreply', ruleData);
      return response.data.rule;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create rule');
    }
  }
);

export const updateEmailRule = createAsyncThunk(
  'emailAutoreply/updateRule',
  async ({ ruleId, updates }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/email-autoreply/${ruleId}`, updates);
      return response.data.rule;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update rule');
    }
  }
);

export const deleteEmailRule = createAsyncThunk(
  'emailAutoreply/deleteRule',
  async ({ ruleId }, { rejectWithValue }) => {
    try {
      await api.delete(`/marketing/email-autoreply/${ruleId}`);
      return ruleId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete rule');
    }
  }
);

export const testEmailRule = createAsyncThunk(
  'emailAutoreply/testRule',
  async ({ ruleId, testRecipientEmail }, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/marketing/email-autoreply/${ruleId}/test`,
        { testRecipientEmail }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to test rule');
    }
  }
);

const initialState = {
  rules: [],
  currentRule: null,
  loading: false,
  error: null,
};

const emailAutoreplySlice = createSlice({
  name: 'emailAutoreply',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectRule: (state, action) => {
      state.currentRule = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Rules
    builder
      .addCase(fetchEmailRules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmailRules.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = action.payload;
      })
      .addCase(fetchEmailRules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch Single Rule
    builder
      .addCase(fetchEmailRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmailRule.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRule = action.payload;
      })
      .addCase(fetchEmailRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create Rule
    builder
      .addCase(createEmailRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEmailRule.fulfilled, (state, action) => {
        state.loading = false;
        state.rules.push(action.payload);
      })
      .addCase(createEmailRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update Rule
    builder
      .addCase(updateEmailRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEmailRule.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.rules.findIndex((r) => r._id === action.payload._id);
        if (index !== -1) {
          state.rules[index] = action.payload;
        }
        if (state.currentRule?._id === action.payload._id) {
          state.currentRule = action.payload;
        }
      })
      .addCase(updateEmailRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete Rule
    builder
      .addCase(deleteEmailRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEmailRule.fulfilled, (state, action) => {
        state.loading = false;
        state.rules = state.rules.filter((r) => r._id !== action.payload);
        if (state.currentRule?._id === action.payload) {
          state.currentRule = null;
        }
      })
      .addCase(deleteEmailRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Test Rule
    builder
      .addCase(testEmailRule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(testEmailRule.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(testEmailRule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, selectRule } = emailAutoreplySlice.actions;
export default emailAutoreplySlice.reducer;
